import { Database } from "@punchup/data/supabase.types";
import { Comedian } from "../legacy/comedian";
import { VenuePagesRow } from "./venue_pages";

export type BookingRow = Database["public"]["Tables"]["bookings"]["Row"];
export type BookingContactRow =
  Database["public"]["Tables"]["booking_contacts"]["Row"];
export type BookingDateRow =
  Database["public"]["Tables"]["booking_dates"]["Row"];
export type BookingResponseRow =
  Database["public"]["Tables"]["booking_responses"]["Row"];
export type BookingContactComedianRow =
  Database["public"]["Tables"]["booking_contact_comedians"]["Row"];
export type BookingContactInvitationRow =
  Database["public"]["Tables"]["booking_contact_invitations"]["Row"];

export enum BookingStatus {
  IN_PROGRESS = "in_progress",
  CONFIRMED = "confirmed",
  CANCELED = "canceled",
  COMPLETED = "completed",
}

export enum BookingResponseStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  DECLINED = "declined",
  INTERESTED = "interested",
  PROCESSED = "processed",
  // New states for performance invitation flow
  INVITED = "invited", // Venue sent performance invitation with fee/position
  CONFIRMED = "confirmed", // Comedian confirmed the performance invitation
  REJECTED = "rejected", // Comedian rejected the performance invitation
}

export interface BookingDate {
  id: string;
  booking_id: string;
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_available: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BookingComedian {
  id: number;
  booking_id: number;
  comedian_id: string;
  email_sent: boolean;
  email_sent_at?: string;
  sms_sent: boolean;
  sms_sent_at?: string;
  created_at: string;
  updated_at: string;
}

export interface BookingResponse {
  id: number;
  booking_id: number;
  comedian_id: string;
  response_status: BookingResponseStatus;
  general_message?: string;
  created_at: string;
  updated_at: string;
}

export interface BookingDateResponse {
  id: number;
  booking_response_id: number;
  booking_date_id: number;
  is_available: boolean;
  message?: string;
  created_at: string;
  updated_at: string;
}

export interface BookingContact {
  id: string;
  venue_page_id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  description?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  comedians?: BookingContactComedian[]; // Added to include associated comedians
}

export interface BookingContactComedian {
  id: string;
  contact_id: string;
  comedian_id: string;
  created_at: string;
  tags?: string[];
  notes?: string;
  tier?: number;
}

export interface BookingContactInvitation {
  id: string;
  booking_id: string;
  booking_contact_id: string;
  invitation_token: string;
  email_sent: boolean;
  email_sent_at?: string | null;
  sms_sent: boolean;
  sms_sent_at?: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
  booking_contact?: BookingContact;
}

export interface BookingContactResponse {
  id: string;
  booking_contact_invitation_id: string;
  comedian_id?: string | null;
  booking_date_ids: string[];
  status:
    | "pending"
    | "accepted"
    | "declined"
    | "interested"
    | "processed"
    | "invited"
    | "confirmed"
    | "rejected";
  message?: string | null;
  contact?: BookingContactWithDetails; // Contact with populated comedian details (may be undefined)
  contact_name?: string | null; // Name of the contact person
  contact_email?: string | null; // Email of the contact person
  metadata?: { show_id?: string }; // Store additional data like associated show ID
  show_id?: string | null; // ID of the show comedian is invited to
  lineup_position?: number | null; // Position in the show lineup
  performance_fee?: number | null; // Performance fee for the show
  performance_time?: string | null; // Time of the performance
  performance_duration?: number | null; // Duration of performance in minutes
  invitation_sent_at?: string | null; // When invitation was sent
  invitation_response_at?: string | null; // When comedian responded
  invitation_token?: string | null; // Token for invitation confirmation
  created_at: string;
  updated_at: string;
  booking_contact_invitation?: BookingContactInvitation;
}

export interface BookingContactWithDetails extends BookingContact {
  comedians: (BookingContactComedian & { comedian: Comedian | null })[];
}

export interface BookingWithDetails extends BookingRow {
  id: string;
  venue_page_id: string;
  venue_page?: VenuePagesRow | null;
  booking_dates?: BookingDate[];
  booking_contact_invitations?: (BookingContactInvitation & {
    contact: BookingContactWithDetails;
  })[];
  booking_contact_responses?: (BookingContactResponse & {
    comedian?: Comedian;
  })[];
}

export interface CreateBookingRequest {
  title: string;
  description?: string;
  venue_page_id: string;
  dates: {
    date: string;
    start_time?: string;
    end_time?: string;
    notes?: string;
  }[];
  comedian_ids?: string[];
  contact_ids?: string[];
  send_to_all_comedians?: boolean;
  additional_message?: string;
}

export interface BookingFormData {
  title: string;
  description: string;
  dates: {
    date: string;
    start_time: string;
    end_time: string;
    notes: string;
  }[];
  selectedComedians: Comedian[];
  selectedContacts: BookingContactWithDetails[];
  sendToAllComedians: boolean;
  additionalMessage: string;
}

export interface BookingStepProps {
  formData: BookingFormData;
  onUpdate: (updates: Partial<BookingFormData>) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onComplete?: () => void;
  isLoading?: boolean;
}
